import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Delete, X } from "lucide-react";
import { unlockReviews } from "../lib/api";
import { ReviewsList } from "./ReviewsList";

const KEYS = [
  ["1", ""], ["2", "ABC"], ["3", "DEF"],
  ["4", "GHI"], ["5", "JKL"], ["6", "MNO"],
  ["7", "PQRS"], ["8", "TUV"], ["9", "WXYZ"],
  ["*", ""], ["0", "+"], ["#", ""],
];
const LEN = 6;

export const LockDial = ({ onClose }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (code.length !== LEN) return undefined;
    let cancelled = false;
    unlockReviews(code)
      .then((res) => !cancelled && setData(res.data))
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setTimeout(() => { setError(false); setCode(""); }, 600);
      });
    return () => { cancelled = true; };
  }, [code]);

  const press = (d) => setCode((c) => (c.length < LEN ? c + d : c));

  return (
    <motion.div
      data-testid="lock-modal"
      initial={{ opacity: 0, y: "6%" }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: "6%" }}
      transition={{ type: "spring", stiffness: 220, damping: 26 }}
      className="absolute inset-0 z-40 bg-[rgba(18,15,12,0.98)] backdrop-blur-xl flex flex-col"
    >
      <button data-testid="close-lock-modal-btn" onClick={onClose} aria-label="Sluiten" className="absolute top-4 right-4 p-2 rounded-full border border-[var(--line)] hover:bg-[var(--paper)] hover:text-[var(--ink)] transition-colors z-10">
        <X className="h-5 w-5" />
      </button>
      {data ? (
        <ReviewsList data={data} />
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Voer toegangscode in</p>
          <motion.div
            data-testid="passcode-input-display"
            animate={error ? { x: [0, -14, 14, -10, 10, 0] } : { x: 0 }}
            transition={{ duration: 0.45 }}
            className="flex gap-4 my-[3vh]"
          >
            {Array.from({ length: LEN }).map((_, i) => (
              <span key={i} className={`h-3.5 w-3.5 rounded-full border transition-colors ${error ? "border-red-400 bg-red-400" : i < code.length ? "bg-[var(--paper)] border-[var(--paper)]" : "border-[rgba(244,237,225,0.6)]"}`} />
            ))}
          </motion.div>
          <div className="grid grid-cols-3 gap-x-[clamp(1rem,6vw,1.75rem)] gap-y-[clamp(0.6rem,2vh,1.1rem)]">
            {KEYS.map(([d, l], i) => (
              <motion.button
                key={d}
                data-testid={`dial-key-btn-${d === "*" ? "star" : d === "#" ? "hash" : d}`}
                onClick={() => press(d)}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.05 + i * 0.03, type: "spring", stiffness: 300, damping: 20 }}
                className="dial-key h-[clamp(3.6rem,9.5vh,5rem)] w-[clamp(3.6rem,9.5vh,5rem)] rounded-full bg-[rgba(244,237,225,0.13)] flex flex-col items-center justify-center leading-none"
              >
                <span className="text-[clamp(1.6rem,4vh,2.2rem)] font-light">{d}</span>
                {l && <span className="text-[10px] font-bold tracking-[0.2em] mt-0.5">{l}</span>}
              </motion.button>
            ))}
          </div>
          <button
            data-testid="dial-delete-btn"
            onClick={() => setCode((c) => c.slice(0, -1))}
            className="mt-[2.5vh] text-sm text-[var(--muted)] hover:text-[var(--paper)] flex items-center gap-2 transition-colors"
          >
            <Delete className="h-4 w-4" /> Wissen
          </button>
        </div>
      )}
    </motion.div>
  );
};
